/* eslint-disable jsx-a11y/anchor-is-valid */
import {
  useState,
  useEffect,
  forwardRef,
  ForwardedRef,
  useImperativeHandle,
} from "react";
import { useTranslation } from "react-i18next";
import {
  DropdownSearch,
  DropdownSearchRadioItem,
} from "../../../../components";
import { Driver } from "../../../../models";
import { useWorkspaceData } from "@data";
import { TableSkeletonsByAmount } from "../../../../components/Skeleton/TableSkeleton";
import { IOrder } from "../../../../types";
import { HelpLabel } from "../../../../components/HelpLabel/HelpLabel";
import { StoryblokHelpBlock } from "../../../../components/StoryblokHelpBlock/StoryblokHelpBlock";

export interface DriverSelectionProps {
  order: IOrder;
  comment?: string;
  emitOnClick?: boolean;
  validationFailed?: boolean;
  allowUnassigned?: boolean;
  update: (driverId: string) => void;
  updateComment?: (comment: string) => void;
}
export interface DriverSelectionRef {
  updateDriver: () => void;
}
export const DriverSelection = forwardRef(
  (props: DriverSelectionProps, ref: ForwardedRef<DriverSelectionRef>) => {
    const { t } = useTranslation("orders");
    const [current, setCurrent] = useState(props.order.driverId);
    const [selectionError, setSelectionError] = useState(false);
    const [searchText, setSearchText] = useState<string>("");
    const [searchResults, setSearchResults] = useState([] as Driver[]);

    const { drivers, loading } = useWorkspaceData();

    /**
     * Exposed to the parent so it can be called through ref.
     */
    useImperativeHandle(ref, () => ({
      updateDriver,
    }));

    function updateDriver() {
      props.update(current || "");
    }

    useEffect(() => {
      if (searchText.length) {
        setSearchResults(
          drivers?.filter((x) =>
            (x.firstName + x.lastName)
              ?.toLowerCase()
              .includes(searchText.toLowerCase()),
          ) || [],
        );
      } else {
        setSearchResults(drivers || []);
      }
    }, [searchText, drivers]);

    const selectDriver = (val: Driver) => {
      // Check if the selected driver has a valid companyId
      if (!val.companyId) {
        setSelectionError(true);
        return;
      }
      setSelectionError(false);
      setCurrent(val.email);

      // In order creation we emit this immediately when selecting.
      // In order details editing we perform the action when parent click 'Save'.
      if (props.emitOnClick) {
        props.update(val.email || "");
      }
    };

    return (
      <div className="bg-white dark:bg-lgb-on-dark-background rounded-lg p-6 border border-border-subtle dark:border-[#2c3353]">
        <div className="flex items-center gap-3 mb-2">
          <h2 className="text-xl font-medium dark:text-white">
            {t("create.driver_selection.title")}
          </h2>
          <HelpLabel
            content={
              <StoryblokHelpBlock
                storySlug="logibud-tms-web/help-popups/use-driver"
                accessToken="Jf9NMAu4DXeo3fwdjd1w7wtt"
              />
            }
            className="mt-1"
          />
        </div>

        <p className="text-secondary dark:text-muted mb-4">
          {t(
            props.allowUnassigned
              ? "create.driver_selection.optional_description"
              : "create.driver_selection.step_description",
          )}
        </p>
        {selectionError && (
          <p role="alert" className="mb-4 text-sm text-danger">
            {t("create.driver_selection.missing_companyId")}
          </p>
        )}

        <div className="mb-6">
          <DropdownSearch
            placeholder={t("create.driver_selection.search_placeholder")}
            inputChanged={(val) => setSearchText(val)}
            showResults
            plain
            divide={false}
          >
            <div>
              {props.allowUnassigned && (
                <DropdownSearchRadioItem
                  id="unassigned"
                  title={t("create.driver_selection.assign_later")}
                  selected={!current}
                  buttonClickEvent={() => {
                    setCurrent("");
                    setSelectionError(false);
                    if (props.emitOnClick) props.update("");
                  }}
                />
              )}
              {loading.drivers && <TableSkeletonsByAmount quantity={6} />}
              {!loading.drivers &&
                searchResults.map((driver: Driver) => {
                  return (
                    <DropdownSearchRadioItem
                      key={driver.id}
                      selected={current === driver.email}
                      buttonClickEvent={() => selectDriver(driver)}
                      id={driver.id}
                      title={driver.firstName + " " + driver.lastName}
                    ></DropdownSearchRadioItem>
                  );
                })}
            </div>
          </DropdownSearch>
        </div>
      </div>
    );
  },
);

export default DriverSelection;
